// Auth-aware HTTP client. Attaches Firebase ID token to every /api request.
import axios from 'axios';
import { getBackendBaseUrl } from '../lib/env';
import { getFirebaseAuth, getIdToken } from './firebase';

export const http = axios.create({ baseURL: getBackendBaseUrl() });

// Shared request interceptor (applied to both dedicated http + global axios)
async function attachAuthHeader(config) {
  try {
    if (process.env.REACT_APP_AUTH_BYPASS === 'true') return config;
    // Only attach for our backend API calls
    const url = (config.baseURL || '') + (config.url || '');
    if (!url.includes('/api/')) return config;
    const token = await getIdToken();
    if (token) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (e) {
    // no token → request will fail with 401 as intended
  }
  return config;
}

async function handleAuthError(err) {
  const status = err?.response?.status;
  const url = err?.config?.url || '';
  const detail = String(err?.response?.data?.detail || '');
  const auth = getFirebaseAuth();
  const hasLocalUser = Boolean(auth?.currentUser);
  const looksLikeHardSessionExpiry = status === 401
    && hasLocalUser
    && /invalid token|token expired|id token has expired|revoked/i.test(detail);
  if (looksLikeHardSessionExpiry && !url.includes('/api/auth/')) {
    window.dispatchEvent(new CustomEvent('signal:session-expired'));
  }
  return Promise.reject(err);
}

http.interceptors.request.use(attachAuthHeader);
http.interceptors.response.use((r) => r, handleAuthError);

// Install same interceptors on the global axios instance so existing
// components that import axios directly automatically get Bearer tokens.
axios.interceptors.request.use(attachAuthHeader);
axios.interceptors.response.use((r) => r, handleAuthError);

export default http;

